import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import { routeManager } from 'core/utils/routes'
import ClusterCapacityAndHealth from 'k8s/components/capacity-and-health/ClusterCapacityAndHealth'
import ClusterDetails from 'k8s/components/dashboard/ClusterDetails'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from 'core/elements/button'
import { MessageTypes } from 'core/components/notifications/model'
import { useToast } from 'core/providers/ToastProvider'
import ApiClient from 'api-client/ApiClient'
import { decodeStr } from 'utils/misc'
import DeployNewCluster from '../../components/clusters-dashboard/DeployNewCluster'
import downloadFile from 'core/utils/downloadFile'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { useSelector } from 'react-redux'
import { kaapiClustersOverviewSelector } from '../../components/kaapi/clusters/selectors'
import { makeStyles } from '@material-ui/core/styles'
import Theme from 'core/themes/model'
import { listKaapiClusters } from '../../components/kaapi/clusters/actions'
import useListAction from 'core/hooks/useListAction'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import PollingData from 'core/components/PollingData'
import Tooltip from 'core/elements/tooltip'
import ConfigureMetalLBModal from '../clusters-dashboard/ConfigureMetalLBModal'
import { listKaapiClusterAddons } from '../kaapi/cluster-addons/actions'

const { kubernetes } = ApiClient.getInstance()

export enum IClusterDetailTabs {
  OVERVIEW = 'overview',
  CAPACITY_AND_HEALTH = 'capacityAndHealth',
}

const ClusterDetailTabs = () => {
  const classes = useStyles()
  const showToast = useToast()
  const [showConfigureMetalLBModal, setShowConfigureMetalLBModal] = useState(false)
  const { prefs: k8sPluginGlobalPerfs, updatePrefs } = useScopedPreferences('k8sPluginGlobalParams')
  const { loading: loadingKaapiClusterAddons, reload: reloadKaapiClusterAddons } = useListAction(
    listKaapiClusterAddons,
  )
  const { loading: loadingKaapiClusters, reload } = useListAction(listKaapiClusters)
  const clusters = useSelector(kaapiClustersOverviewSelector)
  const cluster = useMemo(
    () => clusters?.find((cluster) => cluster.metadata.name === k8sPluginGlobalPerfs?.cluster),
    [clusters, k8sPluginGlobalPerfs?.cluster],
  )

  useEffect(() => {
    if (cluster?.hostedControlPlane?.status?.apiServerUrl) {
      updatePrefs({
        hcp_endpoint: cluster?.hostedControlPlane?.status?.apiServerUrl,
      })
    }
  }, [cluster])

  const handleDownload = async () => {
    try {
      const kubeconfig = await kubernetes.downloadKubeconfig({
        clusterName: cluster?.metadata?.name,
      })
      if (kubeconfig?.['oidc-login.conf']) {
        downloadFile({
          filename: `${cluster?.metadata?.name}.yaml`,
          contents: decodeStr(kubeconfig?.['oidc-login.conf']),
        })
      } else {
        showToast('Failed to download kubeconfig', MessageTypes.error)
      }
    } catch (e) {
      showToast('Failed to download kubeconfig', MessageTypes.error)
    }
  }

  // This is momized to avoid unnecessary re-renders in Modal
  const memoizedCluster = useMemo(() => [cluster], [cluster])

  const onReload = useCallback(async () => {
    await reload(true)
    await reloadKaapiClusterAddons(true)
  }, [reload, reloadKaapiClusterAddons])

  const isControlPlaneReady = useMemo(() => cluster?.hostedControlPlane?.status?.ready, [cluster])

  return (
    <>
      <PollingData
        hidden
        loading={loadingKaapiClusters || loadingKaapiClusterAddons}
        onReload={onReload}
        refreshDuration={1000 * 30}
        pause={showConfigureMetalLBModal}
      />
      {showConfigureMetalLBModal && (
        <ConfigureMetalLBModal
          onClose={() => setShowConfigureMetalLBModal(false)}
          rows={memoizedCluster}
        />
      )}
      <DeployNewCluster data-testid="deploy-new-cluster">
        <Tooltip
          message={
            !cluster?.isMetalLBAddon
              ? 'Selected cluster does not have MetalLB addon configured'
              : ''
          }
        >
          <Button
            icon="gear"
            variant="secondary"
            onClick={() => setShowConfigureMetalLBModal(true)}
            data-testid="expose-lb-button"
            disabled={!cluster?.isMetalLBAddon}
          >
            Expose LB
          </Button>
        </Tooltip>
        <Tooltip
          message={
            !isControlPlaneReady
              ? 'Kubeconfig can only be downloaded when the control plane is ready'
              : ''
          }
        >
          <Button
            icon="download"
            variant="secondary"
            onClick={handleDownload}
            data-testid="download-kubeconfig-button"
            disabled={!isControlPlaneReady}
          >
            Download Kubeconfig
          </Button>
        </Tooltip>
      </DeployNewCluster>
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={reload}
          data-testid="refresh-cluster-button"
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>
      <div className={classes.clusterDetailTabs}>
        <Tabs
          route={routeManager.routes.kubernetes.infrastructure.cluster}
          data-testid="cluster-detail-tabs"
        >
          <Tab label="Overview" value={IClusterDetailTabs.OVERVIEW} data-testid="overview-tab">
            <ClusterDetails />
          </Tab>
          <Tab
            label="Capacity and Health"
            value={IClusterDetailTabs.CAPACITY_AND_HEALTH}
            data-testid="capacity-and-health-tab"
          >
            <ClusterCapacityAndHealth />
          </Tab>
        </Tabs>
      </div>
    </>
  )
}

// CSS
const useStyles = makeStyles<Theme>((theme) => ({
  clusterDetailTabs: {
    marginTop: theme.spacing(1),
  },
}))

export default ClusterDetailTabs
